import { z } from "zod";
import { defineStepConfig } from "../../../registry/helpers";

/** Why a fetched document was not submitted. */
export const PORTAL_REMITTANCE_SKIP_REASONS = [
  /** An advice already exists for this content (an earlier fetch). */
  "already_ingested",
  /** The same content appeared earlier in this fetch. */
  "duplicate_in_fetch",
] as const;

/** What happened to the receipt link of a fetch made for one cash receipt. */
export const PORTAL_RECEIPT_LINK_OUTCOMES = [
  /** The one new remittance was submitted with the receipt; its ingestion links it. */
  "submitted_with_receipt",
  /** The one remittance was ingested before; its advice was linked now. */
  "linked",
  /** The one remittance's advice was already linked to this receipt. */
  "already_linked",
  /** The one remittance's advice is linked to another receipt; left alone. */
  "advice_linked_elsewhere",
  /** The receipt's links already settle it; the advice was left unlinked. */
  "receipt_settled",
  /** Several remittances came back; submitted without the receipt. */
  "several_documents",
  /** Nothing came back. */
  "no_documents",
] as const;

/**
 * Submit Portal Remittance Documents
 *
 * The last step of an "AR Remittance Portal Fetch" workflow, after its Browser
 * Use Agent: hands each document the operator downloaded (an operator
 * download of this organization) to `RemittanceIntakeService.submit()` with
 * channel `portal`, the customer as a hint, and the receipt when the fetch was
 * for one and brought back exactly one remittance (an advice ingested earlier
 * is linked to the receipt directly). One submit (one "AR Remittance Document
 * Ingestion" run) per document.
 *
 * A portal hands back the same file on every fetch, so each document's raw
 * reference is its content hash (`<customerId>:sha256:<hex>`): content that
 * already has an advice, or repeats within this fetch, is skipped rather than
 * ingested again.
 */
export const submitPortalRemittanceDocumentsStep = defineStepConfig({
  type: "submit_portal_remittance_documents",
  name: "Submit Portal Remittance Documents",
  description:
    "Submit each portal-downloaded remittance document for ingestion, skipping content already ingested",
  category: "Data Access",
  libraryModule: "AccountsReceivable",

  inputSchema: z.object({
    customerId: z.uuid().describe("Customer the portal belongs to"),
    documentIds: z
      .array(z.string())
      .default([])
      .describe(
        "Documents the operator downloaded; wire from the Browser Use Agent's documentIds",
      ),
    bankTransactionId: z
      .uuid()
      .optional()
      .describe(
        "The cash receipt the fetch was for; its remittance links to it when exactly one came back",
      ),
  }),

  outputSchema: z.object({
    submittedCount: z.number().int().nonnegative(),
    submissions: z
      .array(
        z.object({
          documentId: z.string(),
          rawReference: z.string(),
          workflowRunId: z.string(),
        }),
      )
      .describe("One ingestion run per submitted document"),
    skipped: z
      .array(
        z.object({
          documentId: z.string(),
          rawReference: z.string(),
          reason: z.enum(PORTAL_REMITTANCE_SKIP_REASONS),
        }),
      )
      .describe("Documents not submitted, and why"),
    receiptLink: z
      .object({
        bankTransactionId: z.uuid(),
        outcome: z.enum(PORTAL_RECEIPT_LINK_OUTCOMES),
        documentId: z.string().nullable(),
        remittanceAdviceId: z.string().nullable(),
      })
      .nullable()
      .describe(
        "For a fetch made for a cash receipt: whether its remittance was linked to it (only when exactly one came back)",
      ),
  }),

  configSchema: z.object({}),

  emits: [] as const,
});
